/**
 * PhotoTile — square thumbnail tile for a single vehicle photo.
 *
 * Renders both committed photos (from the server list) and optimistic/pending
 * tiles (from the local queue). The tile's open-lightbox control is a
 * <button> that fills a non-interactive wrapper <div>; the kebab menu (when
 * present) is a sibling overlay of that button, never a descendant of it —
 * nested interactive controls are invalid HTML/ARIA (WCAG 4.1.2).
 *
 * Image loading strategy:
 *   - Committed photos: lazy-loaded via IntersectionObserver. The fetch only
 *     fires when the tile nears the viewport (threshold 0.1, rootMargin 200px).
 *   - Pending/uploading: shows the local previewUrl (URL.createObjectURL).
 *
 * HEIC that fails to render → quiet "Preview unavailable" tile. The original
 * bytes were stored fine; no upload is blocked.
 *
 * Slice-2 slots (now implemented):
 *   - Top-left:  status badge (upload state OR publish state — mutually exclusive)
 *   - Top-right: kebab overflow menu for committed photos (Publish / Make private / Delete)
 */

import { Pill } from '@/components/Pill';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { usePhotoImage } from '@/hooks/usePhotoImage';
import type { PhotoQueueItem } from '@/lib/photoQueue';
import { format } from 'date-fns';
import { MoreVertical } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Skeleton } from './PhotoSkeleton';

export type DerivativeStatus = 'rendering' | 'ready' | 'failed';

export interface CommittedPhoto {
  photoId: string;
  url: string;
  contentType: string;
  status: 'committed';
  // exactOptionalPropertyTypes: declare as T | undefined to accept both absent
  // and explicitly-undefined values (matching zod .optional() inference).
  capturedAt?: string | undefined;
  caption?: string | undefined;
  createdAt: string;
  // Slice 2/3 publish fields — absent means private (not yet published).
  publicConfirmed?: boolean | undefined;
  derivativeStatus?: DerivativeStatus | undefined;
}

export type PhotoTileItem = CommittedPhoto | (PhotoQueueItem & { type: 'queued' });

export interface PhotoTileActions {
  onPublish: (photoId: string) => void;
  onMakePrivate: (photoId: string) => void;
  onDelete: (photoId: string) => void;
}

interface PhotoTileProps {
  item: PhotoTileItem;
  onOpen: (item: PhotoTileItem) => void;
  /** Slice 2 action callbacks — required to show the kebab on committed tiles. */
  actions?: PhotoTileActions;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return format(new Date(iso), 'd MMM yyyy');
  } catch {
    return '';
  }
}

// ProgressBar renders the 3px magenta bar at the tile bottom.
function ProgressBar({ progress }: { progress: number }) {
  const pct = Math.min(100, Math.max(0, progress));
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: 3,
        width: '100%',
        background: `color-mix(in srgb, ${T.accent} 30%, transparent)`,
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${pct}%`,
          background: T.accent,
          transition: `width ${TRANSITION.base}`,
        }}
      />
    </div>
  );
}

// CommittedTileImage: lazy-loads via IntersectionObserver + usePhotoImage.
function CommittedTileImage({ photo }: { photo: CommittedPhoto }) {
  const [enabled, setEnabled] = useState(false);
  const [imgError, setImgError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgState = usePhotoImage(photo.photoId, enabled);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setEnabled(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
    >
      {imgState.status === 'loading' || imgState.status === 'idle' ? (
        <Skeleton />
      ) : imgState.status === 'ready' && !imgError ? (
        <img
          src={imgState.url}
          alt={photo.caption ?? 'Vehicle photo'}
          onError={() => setImgError(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        // HEIC preview unavailable or error
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: T.card,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: T.textMuted,
              fontFamily: F.body,
              textAlign: 'center',
              padding: SPACE.xs,
            }}
          >
            Preview unavailable
          </span>
        </div>
      )}
    </div>
  );
}

// KebabMenu — overflow menu for committed tile actions.
// Rendered as a portal-less absolutely-positioned menu; closes on outside click.
interface KebabMenuProps {
  photoId: string;
  isPublished: boolean;
  actions: PhotoTileActions;
}

function KebabMenu({ photoId, isPublished, actions }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const triggerStyle: CSSProperties = {
    position: 'absolute',
    top: SPACE.xs,
    right: SPACE.xs,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.55)',
    border: 'none',
    borderRadius: RADIUS.md,
    cursor: 'pointer',
    color: '#fff',
    minWidth: 44,
    minHeight: 44,
    padding: SPACE.xs,
    transition: `background ${TRANSITION.fast}`,
  };

  const menuStyle: CSSProperties = {
    position: 'absolute',
    top: 48,
    right: SPACE.xs,
    zIndex: 10,
    background: T.bg,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
    minWidth: 160,
    overflow: 'hidden',
  };

  const itemStyle: CSSProperties = {
    display: 'block',
    width: '100%',
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: F.body,
    fontSize: 13,
    textAlign: 'left',
    color: T.text,
    minHeight: 44,
    transition: `background ${TRANSITION.fast}`,
  };

  const dangerItemStyle: CSSProperties = {
    ...itemStyle,
    color: T.red,
  };

  return (
    <div ref={menuRef} style={{ position: 'absolute', top: 0, right: 0, zIndex: 2 }}>
      <button
        type="button"
        // clipped-focus: also sits inside PhotoTile's overflow:hidden
        // wrapper — pre-existing clipping, fixed alongside the tile button.
        className="clipped-focus"
        aria-label="Photo actions"
        aria-haspopup="menu"
        aria-expanded={open}
        style={triggerStyle}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <MoreVertical size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div role="menu" aria-label="Photo actions" style={menuStyle}>
          {!isPublished ? (
            <button
              type="button"
              role="menuitem"
              style={itemStyle}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                actions.onPublish(photoId);
              }}
            >
              Publish to share link
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              style={itemStyle}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                actions.onMakePrivate(photoId);
              }}
            >
              Make private
            </button>
          )}
          <div aria-hidden="true" style={{ height: 1, background: T.border }} />
          <button
            type="button"
            role="menuitem"
            style={dangerItemStyle}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              actions.onDelete(photoId);
            }}
          >
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function PhotoTile({ item, onOpen, actions }: PhotoTileProps) {
  const isQueued = 'type' in item && item.type === 'queued';
  const queuedItem = isQueued ? (item as PhotoQueueItem & { type: 'queued' }) : null;
  const committedItem = !isQueued ? (item as CommittedPhoto) : null;

  const isUploading = queuedItem?.status === 'inflight';
  const isPending = queuedItem?.status === 'pending';
  const isFailed = queuedItem?.status === 'failed';
  const isInteractive = !isUploading;

  // Derive publish state for committed photos.
  const isPublished = committedItem?.publicConfirmed === true;
  const derivativeStatus = committedItem?.derivativeStatus;
  const isRenderingPreview = derivativeStatus === 'rendering';

  const caption = committedItem?.caption ?? queuedItem?.caption;
  const capturedAt = committedItem?.capturedAt ?? queuedItem?.capturedAt;
  const displayDate = formatDate(capturedAt);

  // Outer wrapper — non-interactive. Owns the tile's visual box (border,
  // radius, clipping, background) and is the positioning context shared by
  // the open-lightbox button and the kebab overlay, so the two can be DOM
  // siblings while looking pixel-identical to the old nested-button layout.
  const tileContainerStyle: CSSProperties = {
    aspectRatio: '1 / 1',
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    background: T.card,
    opacity: isFailed ? 0.6 : 1,
    transition: `opacity ${TRANSITION.fast}`,
  };

  // Open-lightbox control — fills the wrapper exactly (inset: 0) so every
  // absolutely-positioned child (image, overlay, badges) that used to be
  // positioned relative to the tile button still lines up identically.
  const tileButtonStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    cursor: isInteractive ? 'pointer' : 'default',
    display: 'block',
    background: 'transparent',
    border: 'none',
    padding: 0,
    // Reset button styles
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: 'inherit',
  };

  const overlayStyle: CSSProperties = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
    padding: `${SPACE.sm}px ${SPACE.xs}px ${SPACE.xs}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    pointerEvents: 'none',
  };

  // Determine which badge to show (top-left).
  // Upload states take priority; committed publish states only shown on committed tiles.
  const showUploadBadge = isUploading || isPending || isFailed;
  const showPublishBadge =
    !showUploadBadge && committedItem !== null && (isPublished || isRenderingPreview);

  return (
    <div style={tileContainerStyle}>
      <button
        type="button"
        // clipped-focus: this button fills a position:relative,
        // overflow:hidden wrapper, so a normal outside-the-box focus ring
        // would be clipped and invisible (WCAG 2.4.7). See index.css.
        className="clipped-focus"
        style={tileButtonStyle}
        aria-label={[caption, displayDate].filter(Boolean).join(' · ') || 'Vehicle photo'}
        disabled={!isInteractive}
        onClick={() => isInteractive && onOpen(item)}
      >
        {/* Image / preview */}
        {committedItem ? (
          <CommittedTileImage photo={committedItem} />
        ) : queuedItem?.previewUrl ? (
          <img
            src={queuedItem.previewUrl}
            alt={caption ?? 'Uploading photo'}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <Skeleton />
        )}

        {/* Caption + date overlay */}
        {caption || displayDate ? (
          <div style={overlayStyle} aria-hidden="true">
            {caption ? (
              <span
                style={{
                  fontSize: 12,
                  fontFamily: F.body,
                  color: '#fff',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {caption}
              </span>
            ) : null}
            {displayDate ? (
              <span
                style={{
                  fontSize: 10,
                  fontFamily: F.mono,
                  color: 'rgba(255,255,255,0.8)',
                  display: 'block',
                }}
              >
                {displayDate}
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Status badge — top-left */}
        {showUploadBadge ? (
          <div
            style={{
              position: 'absolute',
              top: SPACE.xs,
              left: SPACE.xs,
              zIndex: 1,
            }}
          >
            {isUploading ? (
              <Pill color="accent" size="sm">
                Uploading
              </Pill>
            ) : isPending ? (
              <Pill color="orange" size="sm">
                Queued
              </Pill>
            ) : isFailed ? (
              <Pill color="red" size="sm">
                Failed
              </Pill>
            ) : null}
          </div>
        ) : showPublishBadge ? (
          <div
            style={{
              position: 'absolute',
              top: SPACE.xs,
              left: SPACE.xs,
              zIndex: 1,
            }}
          >
            {isRenderingPreview ? (
              <Pill color="orange" size="sm">
                Preparing preview
              </Pill>
            ) : isPublished ? (
              <Pill color="green" size="sm">
                Public
              </Pill>
            ) : null}
          </div>
        ) : null}

        {/* Progress bar — bottom of tile during upload */}
        {isUploading && queuedItem?.progress !== undefined ? (
          <ProgressBar progress={queuedItem.progress} />
        ) : null}
      </button>

      {/* Kebab action menu — top-right, committed tiles only. Sibling of the
          tile button (not a descendant) so the two interactive controls
          don't nest — see WCAG 4.1.2 note in the file header. */}
      {committedItem && actions ? (
        <KebabMenu photoId={committedItem.photoId} isPublished={isPublished} actions={actions} />
      ) : null}
    </div>
  );
}
